import ContactUsForm from '@/components/contactUs/contactUsForm';
import DataGridTable from '@/components/datagridTable';
import DrawerPanel from '@/components/drawerPanel';
import ErrorMessage from '@/components/errorMessage';
import ProgressIndicator from '@/components/progressIndicator';
import { useApiContext } from '@/contexts/apiContext';
import { useElementSize } from '@custom-react-hooks/use-element-size';
import LaunchIcon from '@mui/icons-material/Launch';
import { Box, Button, Stack, Typography } from '@mui/material';
import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { formatRelative } from 'date-fns';

const DataSourceDefs = () => {
  const apiContext = useApiContext();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [containerRef, containerSize] = useElementSize();
  const [topPanelRef, topPanelSize] = useElementSize();

  const [dataSourceTypes, setDataSourceTypes] = useState<any>([]);
  const [apiError, setApiError] = useState<any>();
  const [loading, setLoading] = useState(false);

  const [showContactUs, setShowContactUs] = useState(false);

  const hideContactUs = () => {
    setShowContactUs(false);
  };

  useEffect(() => {
    const getDataSourceDefs = async () => {
      try {
        setLoading(true);
        const dataSourceTypes = await apiContext.getDataSourceDefs();
        setDataSourceTypes(dataSourceTypes);
      } catch (error: any) {
        setApiError(error);
      }
      setLoading(false);
    };
    getDataSourceDefs();
  }, []);

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: t('dataSource.dataSourceDefs.columns.id'),
      width: 200,
    },
    {
      field: 'name',
      headerName: t('dataSource.dataSourceDefs.columns.name'),
      flex: 1,
    },
    {
      field: 'domainId',
      headerName: 'Domain',
      width: 140,
      valueFormatter: (value: string) => value || 'general',
    },
    {
      field: 'createdBy',
      headerName: t('dataSource.dataSourceDefs.columns.createdBy'),
      width: 200,
      valueFormatter: (value: string) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : ''),
    },
    {
      field: 'createdDate',
      headerName: t('dataSource.dataSourceDefs.columns.createdDate'),
      width: 200,
      valueFormatter: (value: string) => (value ? formatRelative(new Date(value), new Date()) : ''),
    },
    {
      field: 'actions',
      type: 'actions',
      width: 50,
      getActions: (params: any) => [
        <GridActionsCellItem icon={<LaunchIcon />} label="Open" onClick={() => viewDataSourceDef(params.row)} />,
      ],
    },
  ];

  const viewDataSourceDef = (dataSourceDef: any) => {
    navigate(`/dataSourceDef/${dataSourceDef.id}`);
  };

  const createNew = () => {
    setShowContactUs(true);
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: 1,
        borderColor: 'divider',
        borderRadius: '12px',
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <Stack
        ref={topPanelRef}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ px: 2.5, py: 1.25, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}
      >
        <Stack spacing={0.25}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {t('dataSource.dataSourceDefs.header')}
          </Typography>
          {apiError && <ErrorMessage error={apiError} />}
        </Stack>
        <Button
          size="small"
          color="secondary"
          variant="contained"
          onClick={createNew}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: '8px',
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
            color: 'text.primary',
          }}
        >
          {t('dataSource.dataSourceDefs.actions.create')}
        </Button>
      </Stack>
      <Box sx={{ flex: 1, overflow: 'hidden', p: 1 }}>
        {loading ? (
          <ProgressIndicator />
        ) : (
          <Stack spacing={1} justifyContent="space-between">
            {dataSourceTypes.length === 0 && (
              <Typography sx={{ p: 1 }}>{t('dataSource.dataSourceDefs.noRecord')}</Typography>
            )}
            {dataSourceTypes.length > 0 && (
              <DataGridTable
                rows={dataSourceTypes}
                columns={columns}
                loading={loading}
                height={`${containerSize.height - topPanelSize.height - 10}px`}
              />
            )}
          </Stack>
        )}
        <DrawerPanel open={showContactUs} onClose={hideContactUs} minWidth={'40vw'}>
          <ContactUsForm onClose={() => setShowContactUs(false)} />
        </DrawerPanel>
      </Box>
    </Box>
  );
};

export default DataSourceDefs;
